import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Cancel } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import DialogActionButtons from "../DialogActionButtons";
import DialogHeader from "../DialogHeader";
import LossReasonSelect from "../LossReasonSelect";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  helper: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  }
}));

const normalizeBoardId = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
};

const RegisterLossModal = ({
  open,
  onClose,
  ticketId = null,
  cardId = null,
  boardId = null,
  boards: providedBoards = null,
  onConfirm,
  title = "Registrar perda"
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [lossReasonId, setLossReasonId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canCreateReason = ["admin", "supervisor"].includes(
    String(user?.profile || "").toLowerCase()
  );

  useEffect(() => {
    if (!open) return;

    setLossReasonId("");
    setSubmitted(false);

    const preparedBoards = Array.isArray(providedBoards)
      ? providedBoards.filter(board => board && board.enableOutcomeTracking !== false)
      : [];

    if (preparedBoards.length > 0) {
      setBoards(preparedBoards);
      setSelectedBoardId(normalizeBoardId(boardId) || normalizeBoardId(preparedBoards[0].id));
      return;
    }

    if (boardId) {
      setBoards([]);
      setSelectedBoardId(normalizeBoardId(boardId));
      return;
    }

    const fetchBoards = async () => {
      if (!ticketId && !cardId) return;
      setLoadingBoards(true);
      try {
        const { data } = await api.get("/kanban/cards/boards", {
          params: {
            ...(ticketId ? { ticketId } : {}),
            ...(cardId ? { cardId } : {})
          }
        });
        const outcomeBoards = (data?.boards || []).filter(
          board => board.enableOutcomeTracking
        );
        setBoards(outcomeBoards);
        setSelectedBoardId(outcomeBoards.length ? normalizeBoardId(outcomeBoards[0].id) : "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingBoards(false);
      }
    };

    fetchBoards();
  }, [open, ticketId, cardId, boardId, providedBoards]);

  const shouldShowBoardSelect = boards.length > 1;
  const reasonError = submitted && !lossReasonId;
  const boardError = submitted && !selectedBoardId;

  const selectedBoardLabel = useMemo(() => {
    const board = boards.find(item => Number(item.id) === Number(selectedBoardId));
    return board?.name || "";
  }, [boards, selectedBoardId]);

  const handleConfirm = async () => {
    setSubmitted(true);
    if (!selectedBoardId || !lossReasonId) return;

    const payload = {
      kanbanBoardId: Number(selectedBoardId),
      lossReasonId: Number(lossReasonId)
    };

    setLoading(true);
    try {
      await onConfirm(payload);
      onClose();
    } catch {
      // Parent flows already surface the error and keep their local state consistent.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogHeader icon={<Cancel />} title={title} />
      <DialogContent className={classes.content}>
        <Typography className={classes.helper}>
          Selecione o motivo para registrar esta oportunidade como perdida.
        </Typography>

        {shouldShowBoardSelect && (
          <FormControl variant="outlined" fullWidth error={boardError}>
            <InputLabel>Quadro</InputLabel>
            <Select
              label="Quadro"
              value={selectedBoardId}
              onChange={event => {
                setSelectedBoardId(event.target.value);
                setLossReasonId("");
              }}
              disabled={loadingBoards || loading}
            >
              {boards.map(board => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!shouldShowBoardSelect && selectedBoardLabel ? (
          <Typography variant="caption" color="textSecondary">
            Quadro: {selectedBoardLabel}
          </Typography>
        ) : null}

        <LossReasonSelect
          value={lossReasonId}
          onChange={event => setLossReasonId(event.target.value)}
          kanbanBoardId={selectedBoardId}
          disabled={loadingBoards || loading}
          allowCreate={canCreateReason}
          error={reasonError}
          helperText={reasonError ? "Selecione um motivo de perda." : ""}
        />
      </DialogContent>
      <DialogActionButtons
        onCancel={onClose}
        onSave={handleConfirm}
        loading={loading}
        disabled={loadingBoards}
        saveLabel="Confirmar perda"
      />
    </Dialog>
  );
};

export default RegisterLossModal;
